import type { CatalogGateMeta } from '../services/guestBlogSiteService';

function fmtMoney(n: number) {
  return `$${(Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Free-email accounts — show catalog unlock UI. Business email returns false. */
export function catalogGateAppliesToUser(gate?: Partial<CatalogGateMeta> | null): boolean {
  if (!gate) return false;
  if (typeof gate.catalogGateApplies === 'boolean') return gate.catalogGateApplies;
  if (gate.reason === 'business_email' || gate.reason === 'staff' || gate.reason === 'disabled') {
    return false;
  }
  return gate.reason === 'free_email' || gate.reason === 'unlocked' || Boolean(gate.gated);
}

/** @deprecated Use catalogGateAppliesToUser */
export const needsCatalogUnlock = catalogGateAppliesToUser;

export function getCatalogUnlockRemaining(gate?: Partial<CatalogGateMeta> | null): number {
  if (gate?.unlocked || gate?.canOrder) return 0;
  if (typeof gate?.remainingToUnlock === 'number' && gate.remainingToUnlock >= 0) {
    return gate.remainingToUnlock;
  }
  const min = Number(gate?.unlockMinAmount ?? 50);
  const bal = Number(gate?.walletBalance ?? 0);
  return Math.max(0, min - bal);
}

export function catalogUnlockBlockedMessage(gate?: Partial<CatalogGateMeta> | null): string {
  const remaining = getCatalogUnlockRemaining(gate);
  const min = Number(gate?.unlockMinAmount ?? 50);
  const bal = Number(gate?.walletBalance ?? 0);

  if (remaining <= 0) {
    return 'Add funds once to unlock the full catalog permanently.';
  }
  if (bal > 0) {
    return `You have ${fmtMoney(bal)} in your wallet. Add ${fmtMoney(remaining)} more once to unlock the full catalog permanently (you won’t need to top up again to keep access).`;
  }
  return `Add at least ${fmtMoney(min)} once to unlock the full catalog.`;
}

export function catalogUnlockBannerCopy(gate?: Partial<CatalogGateMeta> | null): {
  title: string;
  body: string;
  buttonLabel: string;
  remaining: number;
} {
  const previewLimit = gate?.previewLimit ?? 10;
  const min = Number(gate?.unlockMinAmount ?? 50);
  const bal = Number(gate?.walletBalance ?? 0);
  const remaining = getCatalogUnlockRemaining(gate);
  const title = `Preview limited to ${previewLimit} sites`;

  if (remaining <= 0) {
    return {
      title,
      body: 'Add funds once to unlock all sites permanently.',
      buttonLabel: 'Add funds',
      remaining: 0,
    };
  }

  if (bal > 0) {
    return {
      title,
      body: `You have ${fmtMoney(bal)}. Add ${fmtMoney(remaining)} more once (min ${fmtMoney(min)} total) to unlock all sites permanently — spending later won’t lock the catalog again.`,
      buttonLabel: `Add ${fmtMoney(remaining)} more`,
      remaining,
    };
  }

  return {
    title,
    body: `Add ${fmtMoney(min)} once to unlock all sites permanently. After unlock, you can use your funds and still browse the full catalog.`,
    buttonLabel: `Add ${fmtMoney(min)} to unlock`,
    remaining,
  };
}

export type WalletFundedNotice = {
  amount?: number;
  balanceAfter?: number;
  unlocked?: boolean;
  remaining?: number;
};

/** Shown on Explore Sites after a wallet top-up returns the user there. */
export function walletFundedThanksCopy(notice: WalletFundedNotice): {
  title: string;
  subtitle: string;
  detail: string;
} {
  if (notice.unlocked) {
    return {
      title: 'Funds added',
      subtitle: 'Thank you. Your wallet is ready.',
      detail: 'You can explore sites and complete your orders.',
    };
  }
  const remaining =
    typeof notice.remaining === 'number' && notice.remaining > 0 ? fmtMoney(notice.remaining) : null;
  return {
    title: 'Funds added',
    subtitle: 'Thank you. Your wallet has been updated.',
    detail: remaining
      ? `Add ${remaining} more once to unlock the full catalog.`
      : 'You can explore sites and continue your orders.',
  };
}

export function walletTopUpSuccessMessage(
  balanceAfter: number,
  gate?: Partial<CatalogGateMeta> | null,
): string {
  const base = `Funds added. New balance: ${fmtMoney(balanceAfter)}.`;
  if (!catalogGateAppliesToUser(gate)) {
    return base;
  }
  if (gate?.unlocked || gate?.canOrder) {
    return `${base} Full catalog unlocked.`;
  }
  const remaining = getCatalogUnlockRemaining(gate);
  if (remaining > 0) {
    return `${base} Add ${fmtMoney(remaining)} more once to unlock the full catalog permanently.`;
  }
  return base;
}

const EXPLORE_RETURN_KEY = 'catalogExploreReturn';

/** Keep in-app paths only, so a stored return target cannot leave the site. */
export function safeInternalPath(value: string | null | undefined): string | null {
  if (!value) return null;
  let path = value.trim();
  try {
    path = decodeURIComponent(path);
  } catch {
    return null;
  }
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('://') || path.includes('\\')) {
    return null;
  }
  return path;
}

export function rememberExploreSitesReturn(path: string) {
  const safe = safeInternalPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(EXPLORE_RETURN_KEY, safe);
  } catch {
    /* ignore */
  }
}

export function readExploreSitesReturn(): string | null {
  try {
    return safeInternalPath(sessionStorage.getItem(EXPLORE_RETURN_KEY));
  } catch {
    return null;
  }
}

export function clearExploreSitesReturn() {
  try {
    sessionStorage.removeItem(EXPLORE_RETURN_KEY);
  } catch {
    /* ignore */
  }
}
